import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  UserGroupCreateType,
  UserGroupType,
  UsersListRequestType,
  UsersListType,
} from '@app/interfaces/users';
import { Button, Input, Paginator, Toggle } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import cn from 'classnames';
import { useCreateUserGroupMutation, useEditUserGroupMutation } from '@app/store/api/users.api';
import RubberTable from '@app/components/rubber-table';

type UserGroupFormPropsType = {
  usersList: UsersListType | undefined;
  onClose?(): void;
  userGroupData?: UserGroupType;
  setUsersBulkRequestData: Dispatch<SetStateAction<Omit<UsersListRequestType, 'filter'>>>;
  usersBulkRequestData: Omit<UsersListRequestType, 'filter'>;
  total?: number;
};

const UserGroupForm: FC<UserGroupFormPropsType> = (props) => {
  const {
    usersList,
    onClose,
    userGroupData,
    setUsersBulkRequestData,
    total,
    usersBulkRequestData,
  } = props;
  const { t } = useTranslation('pages.usersPage');
  const [, trigger] = useState(0);
  useEffect(() => {
    trigger(1);
  }, []);
  // api
  const [createUserGroup] = useCreateUserGroupMutation();
  const [editUserGroup] = useEditUserGroupMutation();
  // api
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserGroupCreateType>({
    defaultValues: userGroupData
      ? { name: userGroupData.name, users: userGroupData.users.map(({ id }) => id) }
      : { name: '', users: [] },
  });

  function onSubmit(data: UserGroupCreateType) {
    userGroupData
      ? editUserGroup({ body: data, params: { id: userGroupData.id } })
          .unwrap()
          .then(() => {
            onClose?.();
          })
      : createUserGroup(data)
          .unwrap()
          .then(() => {
            onClose?.();
          });
  }

  const fieldNameClassName = cn(
    'whitespace-nowrap text-1color text-[15px] font-[500] leading-[110%] tracking-tight',
  );
  const fieldClassName = cn('flex w-full justify-between items-center');

  const tableColumns = [
    {
      title: t('table.userName'),
      index: 'nameCol',
    },
    {
      title: t('table.email'),
      index: 'email',
    },
    // {
    //   title: t('table.role'),
    //   index: 'role',
    // },
    {
      title: t('table.groups'),
      index: 'groups',
    },
    {
      title: t('table.status'),
      index: 'status',
      maxWidth: 150,
    },
  ];

  const tableData = usersList?.users?.map((item) => ({
    nameCol: (
      <div>
        <h1>{item.username}</h1>
      </div>
    ),
    email: (
      <div>
        <h1>{item.login}</h1>
      </div>
    ),
    groups: item.groups.map(({ name }) => name).join(', '),
    status: (
      <div className="max-w-[80px]">
        <Toggle
          onChecked={() => {
            const value = watch('users').includes(item.id)
              ? watch('users').filter((user) => user !== item.id)
              : [...watch('users'), item.id];
            setValue('users', value);
          }}
          checked={watch('users').includes(item.id)}
        />
      </div>
    ),
  }));

  function handleChangePagination({ limit, offset }) {
    setUsersBulkRequestData((prev) => ({ ...prev, limit, offset }));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[20px] relative h-full">
      <div className={fieldClassName}>
        <span className={fieldNameClassName}>{t('users_groups_modal.groupname')}</span>
        <div className="w-[300px]">
          <Input
            {...register('name', { required: { value: true, message: ' ' } })}
            error={errors.name?.message}
          />
        </div>
      </div>
      <div className="bg-basic_app_bg h-full w-full relative p-4 rounded-defaultR flex flex-col gap-[10px]">
        <RubberTable dataSource={tableData || []} columns={tableColumns} />
        <Paginator
          page={Math.ceil((usersBulkRequestData?.offset || 0) + 1)}
          count={total || 100}
          onChange={handleChangePagination}
        />
      </div>
      <div className="flex items-center sticky bg-white bottom-0 h-full py-6 z-[99]">
        <Button type="submit" label={t('users_groups_modal.save_button')} />
        <Button label={t('users_groups_modal.close_button')} fill="linked" onClick={onClose} />
      </div>
    </form>
  );
};

export default UserGroupForm;
